import {
  LanguageCode,
  LocaleDetails,
  toCoreLanguageType,
} from 'shared/src/types/languages.type';

type TutorialStepContent = {
  title: string;
  description: string;
};

type SpotlightStep = {
  title: string;
  description: string;
};

type TutorialUiContent = {
  stepIndicator: string; // e.g., "Step {current} of {total}"
  skipButton: string;
  regionStep: TutorialStepContent;
  searchStep: TutorialStepContent;
  continueWithRegion: string; // e.g., "Continue with {region}"
  searchAnything: string;
  tryDemo: string;
  welcome: string;
  nextButton: string;
  gotIt: string;
  // Spotlight tour steps for region page
  regionSpotlight: {
    globe: SpotlightStep;
    regionList: SpotlightStep;
    yourRegion: SpotlightStep;
  };
  // Spotlight tour steps for search page
  searchSpotlight: {
    searchBar: SpotlightStep;
    demo: SpotlightStep;
    languageSelector: SpotlightStep;
  };
};

// English content
const englishContent: TutorialUiContent = {
  stepIndicator: 'Step {current} of {total}',
  skipButton: 'Skip tutorial',
  regionStep: {
    title: 'Select your region',
    description: 'Choose your location to get the best local products and prices',
  },
  searchStep: {
    title: 'Search for anything',
    description: 'Use our AI-powered search to find and compare products',
  },
  continueWithRegion: 'Continue with {region}',
  searchAnything: 'Start searching',
  tryDemo: 'Try our demo!',
  welcome: 'Welcome to Inception',
  nextButton: 'Next',
  gotIt: 'Got it!',
  regionSpotlight: {
    globe: {
      title: 'Explore the world',
      description: 'Spin the globe and click your country. Its map opens right here. Pick your province or state for local prices, or keep the whole country.',
    },
    regionList: {
      title: 'Quick region selection',
      description: 'Prefer to type? Search for a city or address here, or pick your country straight from the list.',
    },
    yourRegion: {
      title: 'We detected your location!',
      description: 'Click here to continue with products and prices tailored for your region.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-Powered Search',
      description: 'Type anything you\'re looking for. Our AI understands natural language - try "best laptop for gaming under $1000" or "gifts for mom".',
    },
    demo: {
      title: 'Try a demo search',
      description: 'Not sure what to search? Click one of these examples to see how our AI finds and compares the best products for you.',
    },
    languageSelector: {
      title: 'Change language or region',
      description: 'Click here anytime to switch your language or browse products from a different region.',
    },
  },
};

// French content
const frenchContent: TutorialUiContent = {
  stepIndicator: 'Étape {current} sur {total}',
  skipButton: 'Passer le tutoriel',
  regionStep: {
    title: 'Sélectionnez votre région',
    description: 'Choisissez votre emplacement pour obtenir les meilleurs produits et prix locaux',
  },
  searchStep: {
    title: 'Recherchez n\'importe quoi',
    description: 'Utilisez notre recherche alimentée par IA pour trouver et comparer des produits',
  },
  continueWithRegion: 'Continuer avec {region}',
  searchAnything: 'Commencer la recherche',
  tryDemo: 'Essayez notre démo !',
  welcome: 'Bienvenue sur Inception',
  nextButton: 'Suivant',
  gotIt: 'Compris !',
  regionSpotlight: {
    globe: {
      title: 'Explorez le monde',
      description: 'Faites tourner le globe et cliquez sur votre pays. Sa carte s\'ouvre aussitôt : choisissez votre province ou votre État pour des prix locaux, ou gardez le pays entier.',
    },
    regionList: {
      title: 'Sélection rapide de région',
      description: 'Vous préférez écrire ? Cherchez une ville ou une adresse ici, ou choisissez directement votre pays dans la liste.',
    },
    yourRegion: {
      title: 'Nous avons détecté votre position !',
      description: 'Cliquez ici pour continuer avec les produits et prix adaptés à votre région.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Recherche alimentée par IA',
      description: 'Tapez ce que vous cherchez. Notre IA comprend le langage naturel - essayez "meilleur laptop gaming moins de 1000$" ou "cadeaux pour maman".',
    },
    demo: {
      title: 'Essayez une recherche démo',
      description: 'Pas sûr de quoi chercher ? Cliquez sur un de ces exemples pour voir comment notre IA trouve et compare les meilleurs produits pour vous.',
    },
    languageSelector: {
      title: 'Changer de langue ou région',
      description: 'Cliquez ici à tout moment pour changer votre langue ou parcourir les produits d\'une autre région.',
    },
  },
};

// Spanish content
const spanishContent: TutorialUiContent = {
  stepIndicator: 'Paso {current} de {total}',
  skipButton: 'Omitir tutorial',
  regionStep: {
    title: 'Selecciona tu región',
    description: 'Elige tu ubicación para obtener los mejores productos y precios locales',
  },
  searchStep: {
    title: 'Busca lo que quieras',
    description: 'Usa nuestra búsqueda con IA para encontrar y comparar productos',
  },
  continueWithRegion: 'Continuar con {region}',
  searchAnything: 'Empezar a buscar',
  tryDemo: '¡Prueba nuestra demo!',
  welcome: 'Bienvenido a Inception',
  nextButton: 'Siguiente',
  gotIt: '¡Entendido!',
  regionSpotlight: {
    globe: {
      title: 'Explora el mundo',
      description: 'Gira el globo y haz clic en tu país para elegirlo. Vuelve cuando quieras para abrir su mapa y concretar tu provincia o estado, o quedarte con todo el país.',
    },
    regionList: {
      title: 'Selección rápida de región',
      description: '¿Prefieres escribir? Busca aquí una ciudad o una dirección, o elige tu país directamente en la lista.',
    },
    yourRegion: {
      title: '¡Detectamos tu ubicación!',
      description: 'Haz clic aquí para continuar con productos y precios adaptados a tu región.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Búsqueda con IA',
      description: 'Escribe lo que buscas. Nuestra IA entiende lenguaje natural - prueba "mejor laptop gaming menos de $1000" o "regalos para mamá".',
    },
    demo: {
      title: 'Prueba una búsqueda demo',
      description: '¿No sabes qué buscar? Haz clic en uno de estos ejemplos para ver cómo nuestra IA encuentra y compara los mejores productos.',
    },
    languageSelector: {
      title: 'Cambiar idioma o región',
      description: 'Haz clic aquí en cualquier momento para cambiar tu idioma o explorar productos de otra región.',
    },
  },
};

// German content
const germanContent: TutorialUiContent = {
  stepIndicator: 'Schritt {current} von {total}',
  skipButton: 'Tutorial überspringen',
  regionStep: {
    title: 'Wählen Sie Ihre Region',
    description: 'Wählen Sie Ihren Standort für die besten lokalen Produkte und Preise',
  },
  searchStep: {
    title: 'Suchen Sie nach allem',
    description: 'Nutzen Sie unsere KI-gestützte Suche, um Produkte zu finden und zu vergleichen',
  },
  continueWithRegion: 'Weiter mit {region}',
  searchAnything: 'Suche starten',
  tryDemo: 'Probieren Sie unsere Demo!',
  welcome: 'Willkommen bei Inception',
  nextButton: 'Weiter',
  gotIt: 'Verstanden!',
  regionSpotlight: {
    globe: {
      title: 'Erkunden Sie die Welt',
      description: 'Drehen Sie den Globus und klicken Sie auf Ihr Land, um es auszuwählen. Kommen Sie jederzeit zurück, um die Karte dieses Landes zu öffnen und Ihr Bundesland oder Ihre Provinz zu wählen — oder es beim ganzen Land zu belassen.',
    },
    regionList: {
      title: 'Schnelle Regionsauswahl',
      description: 'Lieber tippen? Suchen Sie hier nach einer Stadt oder Adresse, oder wählen Sie Ihr Land direkt aus der Liste.',
    },
    yourRegion: {
      title: 'Wir haben Ihren Standort erkannt!',
      description: 'Klicken Sie hier, um mit Produkten und Preisen fortzufahren, die auf Ihre Region zugeschnitten sind.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'KI-gestützte Suche',
      description: 'Geben Sie ein, wonach Sie suchen. Unsere KI versteht natürliche Sprache - probieren Sie "bester Gaming-Laptop unter 1000€" oder "Geschenke für Mama".',
    },
    demo: {
      title: 'Probieren Sie eine Demo-Suche',
      description: 'Nicht sicher, wonach Sie suchen sollen? Klicken Sie auf eines dieser Beispiele, um zu sehen, wie unsere KI die besten Produkte findet und vergleicht.',
    },
    languageSelector: {
      title: 'Sprache oder Region ändern',
      description: 'Klicken Sie hier jederzeit, um Ihre Sprache zu ändern oder Produkte aus einer anderen Region zu durchsuchen.',
    },
  },
};

// Italian content
const italianContent: TutorialUiContent = {
  stepIndicator: 'Passo {current} di {total}',
  skipButton: 'Salta il tutorial',
  regionStep: {
    title: 'Seleziona la tua regione',
    description: 'Scegli la tua posizione per ottenere i migliori prodotti e prezzi locali',
  },
  searchStep: {
    title: 'Cerca qualsiasi cosa',
    description: 'Usa la nostra ricerca basata su IA per trovare e confrontare i prodotti',
  },
  continueWithRegion: 'Continua con {region}',
  searchAnything: 'Inizia la ricerca',
  tryDemo: 'Prova la nostra demo!',
  welcome: 'Benvenuto su Inception',
  nextButton: 'Avanti',
  gotIt: 'Capito!',
  regionSpotlight: {
    globe: {
      title: 'Esplora il mondo',
      description: 'Ruota il globo e clicca sul tuo Paese per sceglierlo. Torna quando vuoi per aprire la sua mappa e indicare la tua regione, oppure per tenere tutto il Paese.',
    },
    regionList: {
      title: 'Selezione rapida della regione',
      description: 'Preferisci scrivere? Cerca qui una città o un indirizzo, oppure scegli il tuo Paese direttamente dall\'elenco.',
    },
    yourRegion: {
      title: 'Abbiamo rilevato la tua posizione!',
      description: 'Clicca qui per continuare con prodotti e prezzi adattati alla tua regione.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Ricerca basata su IA',
      description: 'Digita ciò che stai cercando. La nostra IA comprende il linguaggio naturale - prova "miglior laptop da gaming sotto 1000€" o "regali per la mamma".',
    },
    demo: {
      title: 'Prova una ricerca demo',
      description: 'Non sai cosa cercare? Clicca su uno di questi esempi per vedere come la nostra IA trova e confronta i migliori prodotti per te.',
    },
    languageSelector: {
      title: 'Cambia lingua o regione',
      description: 'Clicca qui in qualsiasi momento per cambiare la tua lingua o sfogliare prodotti da un\'altra regione.',
    },
  },
};

// Brazilian Portuguese content
const portugueseBRContent: TutorialUiContent = {
  stepIndicator: 'Passo {current} de {total}',
  skipButton: 'Pular tutorial',
  regionStep: {
    title: 'Selecione sua região',
    description: 'Escolha sua localização para obter os melhores produtos e preços locais',
  },
  searchStep: {
    title: 'Pesquise qualquer coisa',
    description: 'Use nossa pesquisa com IA para encontrar e comparar produtos',
  },
  continueWithRegion: 'Continuar com {region}',
  searchAnything: 'Começar a pesquisar',
  tryDemo: 'Experimente nossa demo!',
  welcome: 'Bem-vindo ao Inception',
  nextButton: 'Próximo',
  gotIt: 'Entendi!',
  regionSpotlight: {
    globe: {
      title: 'Explore o mundo',
      description: 'Gire o globo e clique no seu país para escolhê-lo. Volte quando quiser para abrir o mapa dele e escolher o seu estado, ou para ficar com o país inteiro.',
    },
    regionList: {
      title: 'Seleção rápida de região',
      description: 'Prefere digitar? Busque uma cidade ou endereço aqui, ou escolha o seu país direto na lista.',
    },
    yourRegion: {
      title: 'Detectamos sua localização!',
      description: 'Clique aqui para continuar com produtos e preços adaptados para sua região.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Pesquisa com IA',
      description: 'Digite o que você está procurando. Nossa IA entende linguagem natural - tente "melhor laptop para jogos abaixo de R$5000" ou "presentes para mamãe".',
    },
    demo: {
      title: 'Experimente uma pesquisa demo',
      description: 'Não sabe o que pesquisar? Clique em um desses exemplos para ver como nossa IA encontra e compara os melhores produtos para você.',
    },
    languageSelector: {
      title: 'Mudar idioma ou região',
      description: 'Clique aqui a qualquer momento para mudar seu idioma ou navegar por produtos de outra região.',
    },
  },
};

// European Portuguese content
const portuguesePTContent: TutorialUiContent = {
  stepIndicator: 'Passo {current} de {total}',
  skipButton: 'Saltar tutorial',
  regionStep: {
    title: 'Selecione a sua região',
    description: 'Escolha a sua localização para obter os melhores produtos e preços locais',
  },
  searchStep: {
    title: 'Pesquise qualquer coisa',
    description: 'Use a nossa pesquisa com IA para encontrar e comparar produtos',
  },
  continueWithRegion: 'Continuar com {region}',
  searchAnything: 'Começar a pesquisar',
  tryDemo: 'Experimente a nossa demo!',
  welcome: 'Bem-vindo ao Inception',
  nextButton: 'Próximo',
  gotIt: 'Percebi!',
  regionSpotlight: {
    globe: {
      title: 'Explore o mundo',
      description: 'Rode o globo e clique no seu país para o escolher. Volte quando quiser para abrir o mapa dele e indicar a sua região, ou para ficar com o país inteiro.',
    },
    regionList: {
      title: 'Seleção rápida de região',
      description: 'Prefere escrever? Procure aqui uma cidade ou morada, ou escolha o seu país diretamente na lista.',
    },
    yourRegion: {
      title: 'Detetámos a sua localização!',
      description: 'Clique aqui para continuar com produtos e preços adaptados para a sua região.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Pesquisa com IA',
      description: 'Escreva o que está à procura. A nossa IA compreende linguagem natural - experimente "melhor portátil para jogos abaixo de 1000€" ou "presentes para a mãe".',
    },
    demo: {
      title: 'Experimente uma pesquisa demo',
      description: 'Não sabe o que pesquisar? Clique num destes exemplos para ver como a nossa IA encontra e compara os melhores produtos para si.',
    },
    languageSelector: {
      title: 'Mudar idioma ou região',
      description: 'Clique aqui a qualquer momento para mudar o seu idioma ou navegar por produtos de outra região.',
    },
  },
};

// Scottish Gaelic content
const scottishGaelicContent: TutorialUiContent = {
  stepIndicator: 'Ceum {current} de {total}',
  skipButton: 'Leum seachad air an oideachadh',
  regionStep: {
    title: 'Tagh do sgìre',
    description: 'Tagh d\' àite gus na batharan agus prìsean ionadail as fheàrr fhaighinn',
  },
  searchStep: {
    title: 'Lorg rud sam bith',
    description: 'Cleachd an lorg cumhachdach AI againn gus batharan a lorg agus a choimeas',
  },
  continueWithRegion: 'Lean air adhart le {region}',
  searchAnything: 'Tòisich a\' lorg',
  tryDemo: 'Feuch ar demo!',
  welcome: 'Fàilte gu Inception',
  nextButton: 'Air adhart',
  gotIt: 'Tha mi a\' tuigsinn!',
  regionSpotlight: {
    globe: {
      title: 'Rannsaich an saoghal',
      description: 'Cuir an cruinne-cè mun cuairt gus a h-uile sgìre a tha ri fhaighinn fhaicinn. Briog air dùthaich sam bith a tha air a chomharrachadh gus a taghadh.',
    },
    regionList: {
      title: 'Taghadh luath sgìre',
      description: 'No dìreach briog air sgìre bhon liosta seo.',
    },
    yourRegion: {
      title: 'Lorg sinn d\' àite!',
      description: 'Briog an seo gus leantainn le batharan agus prìsean a tha air an dèanamh airson do sgìre.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Lorg le Cumhachd AI',
      description: 'Sgrìobh rud sam bith a tha thu a\' lorg. Tha an AI againn a\' tuigsinn cànan nàdarra - feuch "an laptop as fheàrr airson gheamannan fo $1000" no "tiodhlacan airson mamaidh".',
    },
    demo: {
      title: 'Feuch lorg demo',
      description: 'Chan eil fhios agad dè a lorgas tu? Briog air tè de na h-eisimpleirean seo gus faicinn mar a lorgas ar AI na batharan as fheàrr dhut agus gan coimeas.',
    },
    languageSelector: {
      title: 'Atharraich cànan no sgìre',
      description: 'Briog an seo aig àm sam bith gus do chànan atharrachadh no batharan bho sgìre eile a bhrabhsadh.',
    },
  },
};

// Swedish content
const swedishContent: TutorialUiContent = {
  stepIndicator: 'Steg {current} av {total}',
  skipButton: 'Hoppa över guiden',
  regionStep: {
    title: 'Välj din region',
    description: 'Välj din plats för att få de bästa lokala produkterna och priserna',
  },
  searchStep: {
    title: 'Sök efter vad som helst',
    description: 'Använd vår AI-drivna sökning för att hitta och jämföra produkter',
  },
  continueWithRegion: 'Fortsätt med {region}',
  searchAnything: 'Börja söka',
  tryDemo: 'Testa vår demo!',
  welcome: 'Välkommen till Inception',
  nextButton: 'Nästa',
  gotIt: 'Uppfattat!',
  regionSpotlight: {
    globe: {
      title: 'Utforska världen',
      description: 'Snurra globen och klicka på ditt land för att välja det. Kom tillbaka när du vill för att öppna landets karta och välja din region — eller behålla hela landet.',
    },
    regionList: {
      title: 'Snabbval av region',
      description: 'Vill du hellre skriva? Sök efter en stad eller adress här, eller välj ditt land direkt i listan.',
    },
    yourRegion: {
      title: 'Vi har identifierat din plats!',
      description: 'Klicka här för att fortsätta med produkter och priser anpassade för din region.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-driven sökning',
      description: 'Skriv vad du letar efter. Vår AI förstår naturligt språk – prova "bästa gamingdatorn under 10 000 kr" eller "presenter till mamma".',
    },
    demo: {
      title: 'Testa en demosökning',
      description: 'Osäker på vad du ska söka efter? Klicka på ett av dessa exempel för att se hur vår AI hittar och jämför de bästa produkterna åt dig.',
    },
    languageSelector: {
      title: 'Byt språk eller region',
      description: 'Klicka här när som helst för att byta språk eller bläddra bland produkter från en annan region.',
    },
  },
};

// Welsh content
const welshContent: TutorialUiContent = {
  stepIndicator: 'Cam {current} o {total}',
  skipButton: 'Hepgor y tiwtorial',
  regionStep: {
    title: 'Dewiswch eich rhanbarth',
    description: 'Dewiswch eich lleoliad i gael y cynhyrchion a\'r prisiau lleol gorau',
  },
  searchStep: {
    title: 'Chwiliwch am unrhyw beth',
    description: 'Defnyddiwch ein chwiliad a yrrir gan AI i ddod o hyd i gynhyrchion a\'u cymharu',
  },
  continueWithRegion: 'Parhau gyda {region}',
  searchAnything: 'Dechrau chwilio',
  tryDemo: 'Rhowch gynnig ar ein demo!',
  welcome: 'Croeso i Inception',
  nextButton: 'Nesaf',
  gotIt: 'Deall!',
  regionSpotlight: {
    globe: {
      title: 'Archwiliwch y byd',
      description: 'Trowch y glôb i weld yr holl ranbarthau sydd ar gael. Cliciwch ar unrhyw wlad sydd wedi\'i hamlygu i\'w dewis.',
    },
    regionList: {
      title: 'Dewis rhanbarth yn gyflym',
      description: 'Neu cliciwch ar ranbarth o\'r rhestr hon yn syml.',
    },
    yourRegion: {
      title: 'Rydym wedi canfod eich lleoliad!',
      description: 'Cliciwch yma i barhau gyda chynhyrchion a phrisiau wedi\'u teilwra ar gyfer eich rhanbarth.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Chwiliad a Yrrir gan AI',
      description: 'Teipiwch unrhyw beth rydych yn chwilio amdano. Mae ein AI yn deall iaith naturiol - rhowch gynnig ar "gliniadur gorau ar gyfer gemau o dan £1000" neu "anrhegion i fam".',
    },
    demo: {
      title: 'Rhowch gynnig ar chwiliad demo',
      description: 'Ddim yn siŵr beth i chwilio amdano? Cliciwch ar un o\'r enghreifftiau hyn i weld sut mae ein AI yn dod o hyd i\'r cynhyrchion gorau ac yn eu cymharu ar eich cyfer.',
    },
    languageSelector: {
      title: 'Newid iaith neu ranbarth',
      description: 'Cliciwch yma ar unrhyw adeg i newid eich iaith neu bori drwy gynhyrchion o ranbarth arall.',
    },
  },
};

// Galician content
const galicianContent: TutorialUiContent = {
  stepIndicator: 'Paso {current} de {total}',
  skipButton: 'Saltar titorial',
  regionStep: {
    title: 'Seleccione a súa rexión',
    description: 'Escolla a súa localización para obter os mellores produtos e prezos locais',
  },
  searchStep: {
    title: 'Busque calquera cousa',
    description: 'Use a nosa busca con IA para atopar e comparar produtos',
  },
  continueWithRegion: 'Continuar con {region}',
  searchAnything: 'Comezar a buscar',
  tryDemo: 'Probe a nosa demo!',
  welcome: 'Benvido a Inception',
  nextButton: 'Seguinte',
  gotIt: 'Entendido!',
  regionSpotlight: {
    globe: {
      title: 'Explore o mundo',
      description: 'Xire o globo para ver todas as rexións dispoñibles. Prema en calquera país destacado para seleccionalo.',
    },
    regionList: {
      title: 'Selección rápida de rexión',
      description: 'Ou simplemente prema nunha rexión desta lista.',
    },
    yourRegion: {
      title: 'Detectamos a súa localización!',
      description: 'Prema aquí para continuar con produtos e prezos adaptados á súa rexión.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Busca con IA',
      description: 'Escriba o que está a buscar. A nosa IA comprende a linguaxe natural - probe "mellor portátil para xogos por menos de 1000€" ou "agasallos para mamá".',
    },
    demo: {
      title: 'Probe unha busca de demostración',
      description: 'Non sabe que buscar? Prema nun destes exemplos para ver como a nosa IA atopa e compara os mellores produtos para vostede.',
    },
    languageSelector: {
      title: 'Cambiar idioma ou rexión',
      description: 'Prema aquí en calquera momento para cambiar o seu idioma ou explorar produtos doutra rexión.',
    },
  },
};

// Tamil content
const tamilContent: TutorialUiContent = {
  stepIndicator: 'படி {current} / {total}',
  skipButton: 'பயிற்சிகையை தவிர்',
  regionStep: {
    title: 'உங்கள் பிராந்தியத்தைத் தேர்ந்தெடுங்கள்',
    description: 'சிறந்த உள்ளூர் பொருட்களையும் விலைகளையும் பெற உங்கள் இருப்பிடத்தைத் தேர்ந்தெடுங்கள்',
  },
  searchStep: {
    title: 'எதையும் தேடுங்கள்',
    description: 'பொருட்களைக் கண்டறிந்து ஒப்பிடுவதற்கு எங்கள் AI-இயக்கப்படும் தேடலைப் பயன்படுத்துங்கள்',
  },
  continueWithRegion: '{region} உடன் தொடருங்கள்',
  searchAnything: 'தேடலைத் தொடங்கு',
  tryDemo: 'எங்கள் டெமோவை முயற்சிக்கவும்!',
  welcome: 'Inception-க்கு வரவேற்கிறோம்',
  nextButton: 'அடுத்து',
  gotIt: 'புரிந்தது!',
  regionSpotlight: {
    globe: {
      title: 'உலகை ஆராயுங்கள்',
      description: 'அனைத்து கிடைக்கும் பிராந்தியங்களையும் பார்க்க புவிகோளத்தைச் சுழற்றுங்கள். தேர்ந்தெடுக்க முன்னிலைப்படுத்தப்பட்ட எந்த நாட்டையும் கிளிக் செய்யுங்கள்.',
    },
    regionList: {
      title: 'விரைவு பிராந்தியத் தேர்வு',
      description: 'அல்லது இந்தப் பட்டியலிலிருந்து ஒரு பிராந்தியத்தை கிளிக் செய்யுங்கள்.',
    },
    yourRegion: {
      title: 'உங்கள் இருப்பிடத்தைக் கண்டறிந்தோம்!',
      description: 'உங்கள் பிராந்தியத்திற்கு ஏற்ற பொருட்கள் மற்றும் விலைகளுடன் தொடர இங்கே கிளிக் செய்யுங்கள்.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-இயக்கப்படும் தேடல்',
      description: 'நீங்கள் தேடுவதை தட்டச்சு செய்யுங்கள். எங்கள் AI இயற்கையான மொழியைப் புரிந்துகொள்ளும் - "₹50000-க்கு கீழ் சிறந்த கேமிங் லேப்டாப்" அல்லது "அம்மாவுக்கான பரிசுகள்" என்று முயற்சிக்கவும்.',
    },
    demo: {
      title: 'டெமோ தேடலை முயற்சிக்கவும்',
      description: 'என்ன தேடுவது என்று தெரியவில்லையா? எங்கள் AI உங்களுக்கான சிறந்த பொருட்களை எப்படி கண்டுபிடித்து ஒப்பிடுகிறது என்பதைக் காண இந்த எடுத்துக்காட்டுகளில் ஒன்றைக் கிளிக் செய்யுங்கள்.',
    },
    languageSelector: {
      title: 'மொழி அல்லது பிராந்தியத்தை மாற்றுங்கள்',
      description: 'உங்கள் மொழியை மாற்ற அல்லது வேறு பிராந்தியத்தின் பொருட்களை உலாவ எந்த நேரத்திலும் இங்கே கிளிக் செய்யுங்கள்.',
    },
  },
};

// Polish content
const polishContent: TutorialUiContent = {
  stepIndicator: 'Krok {current} z {total}',
  skipButton: 'Pomiń samouczek',
  regionStep: {
    title: 'Wybierz swój region',
    description: 'Wybierz lokalizację, aby uzyskać najlepsze lokalne produkty i ceny',
  },
  searchStep: {
    title: 'Szukaj czegokolwiek',
    description: 'Użyj naszej wyszukiwarki opartej na AI, aby znaleźć i porównać produkty',
  },
  continueWithRegion: 'Kontynuuj z {region}',
  searchAnything: 'Zacznij szukać',
  tryDemo: 'Wypróbuj nasze demo!',
  welcome: 'Witamy w Inception',
  nextButton: 'Dalej',
  gotIt: 'Rozumiem!',
  regionSpotlight: {
    globe: {
      title: 'Odkrywaj świat',
      description: 'Obróć globus i kliknij swój kraj, aby go wybrać. Wróć tu, kiedy zechcesz, aby otworzyć jego mapę i wskazać swój region — albo zostać przy całym kraju.',
    },
    regionList: {
      title: 'Szybki wybór regionu',
      description: 'Wolisz pisać? Wyszukaj tutaj miasto lub adres albo wybierz swój kraj wprost z listy.',
    },
    yourRegion: {
      title: 'Wykryliśmy Twoją lokalizację!',
      description: 'Kliknij tutaj, aby kontynuować z produktami i cenami dostosowanymi do Twojego regionu.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Wyszukiwarka oparta na AI',
      description: 'Wpisz to, czego szukasz. Nasza AI rozumie język naturalny — spróbuj „najlepszy laptop do gier poniżej 4000 zł" lub „prezenty dla mamy".',
    },
    demo: {
      title: 'Wypróbuj wyszukiwanie demo',
      description: 'Nie wiesz, czego szukać? Kliknij jeden z tych przykładów, aby zobaczyć, jak nasza AI znajduje i porównuje najlepsze produkty.',
    },
    languageSelector: {
      title: 'Zmień język lub region',
      description: 'Kliknij tutaj w dowolnym momencie, aby zmienić język lub przeglądać produkty z innego regionu.',
    },
  },
};

// Dutch content
const dutchContent: TutorialUiContent = {
  stepIndicator: 'Stap {current} van {total}',
  skipButton: 'Handleiding overslaan',
  regionStep: {
    title: 'Selecteer uw regio',
    description: 'Kies uw locatie voor de beste lokale producten en prijzen',
  },
  searchStep: {
    title: 'Zoek naar alles',
    description: 'Gebruik onze AI-gestuurde zoekmachine om producten te vinden en te vergelijken',
  },
  continueWithRegion: 'Doorgaan met {region}',
  searchAnything: 'Begin met zoeken',
  tryDemo: 'Probeer onze demo!',
  welcome: 'Welkom bij Inception',
  nextButton: 'Volgende',
  gotIt: 'Begrepen!',
  regionSpotlight: {
    globe: {
      title: 'Verken de wereld',
      description: 'Draai de wereldbol en klik op uw land om het te kiezen. Kom altijd terug om de kaart van dat land te openen en uw provincie of regio te kiezen — of het hele land te houden.',
    },
    regionList: {
      title: 'Snelle regioselectie',
      description: 'Liever typen? Zoek hier een stad of adres, of kies uw land rechtstreeks uit de lijst.',
    },
    yourRegion: {
      title: 'Wij hebben uw locatie gedetecteerd!',
      description: 'Klik hier om door te gaan met producten en prijzen afgestemd op uw regio.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-gestuurde zoekmachine',
      description: 'Typ wat u zoekt. Onze AI begrijpt natuurlijke taal – probeer "beste laptop voor gaming onder €1000" of "cadeaus voor mama".',
    },
    demo: {
      title: 'Probeer een demozoekopdracht',
      description: 'Weet u niet wat u moet zoeken? Klik op een van deze voorbeelden om te zien hoe onze AI de beste producten voor u vindt en vergelijkt.',
    },
    languageSelector: {
      title: 'Taal of regio wijzigen',
      description: 'Klik hier op elk moment om uw taal te wijzigen of producten uit een andere regio te bekijken.',
    },
  },
};

// Catalan content
const catalanContent: TutorialUiContent = {
  stepIndicator: 'Pas {current} de {total}',
  skipButton: 'Saltar el tutorial',
  regionStep: {
    title: 'Seleccioneu la vostra regió',
    description: 'Trieu la vostra ubicació per obtenir els millors productes i preus locals',
  },
  searchStep: {
    title: 'Cerqueu qualsevol cosa',
    description: "Utilitzeu la nostra cerca basada en IA per trobar i comparar productes",
  },
  continueWithRegion: 'Continuar amb {region}',
  searchAnything: 'Començar a cercar',
  tryDemo: 'Proveu la nostra demo!',
  welcome: 'Benvinguts a Inception',
  nextButton: 'Següent',
  gotIt: 'Entès!',
  regionSpotlight: {
    globe: {
      title: 'Exploreu el món',
      description: "Gireu el globus per veure totes les regions disponibles. Feu clic a qualsevol país ressaltat per seleccionar-lo.",
    },
    regionList: {
      title: 'Selecció ràpida de regió',
      description: "O simplement feu clic a una regió d'aquesta llista.",
    },
    yourRegion: {
      title: 'Hem detectat la vostra ubicació!',
      description: 'Feu clic aquí per continuar amb productes i preus adaptats a la vostra regió.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Cerca basada en IA',
      description: 'Escriviu el que cerqueu. La nostra IA entén el llenguatge natural - proveu "millor portàtil per a jocs per menys de 1000€" o "regals per a la mare".',
    },
    demo: {
      title: 'Proveu una cerca de demo',
      description: 'No sabeu què cercar? Feu clic en un d\'aquests exemples per veure com la nostra IA troba i compara els millors productes.',
    },
    languageSelector: {
      title: 'Canviar idioma o regió',
      description: "Feu clic aquí en qualsevol moment per canviar el vostre idioma o explorar productes d'una altra regió.",
    },
  },
};

// Māori content
const maoriContent: TutorialUiContent = {
  stepIndicator: 'Hipanga {current} o {total}',
  skipButton: 'Peke i te aratohu',
  regionStep: {
    title: 'Kōwhiria tō rohe',
    description: 'Kōwhiria tō wāhi kia whiwhi i ngā hua me ngā utu ā-rohe pai rawa',
  },
  searchStep: {
    title: 'Rapua te mea katoa',
    description: 'Whakamahia tā mātou rapu AI kia kitea me te whakataurite i ngā hua',
  },
  continueWithRegion: 'Haere tonu me {region}',
  searchAnything: 'Tīmata te rapu',
  tryDemo: 'Whakamātauria tā mātou whakaaturanga!',
  welcome: 'Nau mai ki Inception',
  nextButton: 'Panuku',
  gotIt: 'Kua mārama!',
  regionSpotlight: {
    globe: {
      title: 'Torohia te ao',
      description: 'Hurihia te ao kia kitea ngā rohe e wātea ana. Pāwhiria tētahi whenua kua mīharotia kia kōwhiria.',
    },
    regionList: {
      title: 'Kōwhiringa tere rohe',
      description: 'Pāwhiria rānei tētahi rohe mai i tēnei rārangi.',
    },
    yourRegion: {
      title: 'Kua kitea tō wāhi!',
      description: 'Pāwhiria ki konei kia haere tonu me ngā hua me ngā utu e hāngai ana ki tō rohe.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Rapu AI',
      description: 'Patopato i te mea e rapu ana koe. Ka mārama tā mātou AI ki te reo māori — whakamātauria "rorohiko pai rawa mō te tākaro i raro i te $1000" rānei "koha mō māmā".',
    },
    demo: {
      title: 'Whakamātauria he rapu whakaaturanga',
      description: 'Kāore e tino mōhio he aha hei rapu? Pāwhiria tētahi o ēnei tauira kia kitea me pēhea tā mātou AI e kimi ana me te whakataurite i ngā hua pai rawa mōu.',
    },
    languageSelector: {
      title: 'Hurihia te reo, te rohe rānei',
      description: 'Pāwhiria ki konei i ngā wā katoa kia huri i tō reo, kia tirotiro rānei i ngā hua mai i tētahi atu rohe.',
    },
  },
};

// Irish content
const irishContent: TutorialUiContent = {
  stepIndicator: 'Céim {current} as {total}',
  skipButton: 'Scipeáil an teagaisc',
  regionStep: {
    title: 'Roghnaigh do réigiún',
    description: 'Roghnaigh do shuíomh chun na táirgí agus na praghsanna áitiúla is fearr a fháil',
  },
  searchStep: {
    title: 'Cuardaigh rud ar bith',
    description: 'Úsáid ár gcuardach cumhachtaithe ag AI chun táirgí a aimsiú agus a chur i gcomparáid',
  },
  continueWithRegion: 'Lean ar aghaidh le {region}',
  searchAnything: 'Tosaigh ag cuardach',
  tryDemo: 'Bain triail as ár dtaispeántas!',
  welcome: 'Fáilte go Inception',
  nextButton: 'Ar aghaidh',
  gotIt: 'Tuigim!',
  regionSpotlight: {
    globe: {
      title: 'Taiscéal an domhan',
      description: 'Cas an cruinneog chun gach réigiún atá ar fáil a fheiceáil. Cliceáil ar aon tír atá aibhsithe chun í a roghnú.',
    },
    regionList: {
      title: 'Roghnú tapa réigiúin',
      description: 'Nó cliceáil go simplí ar réigiún ón liosta seo.',
    },
    yourRegion: {
      title: 'Bhraitheamar do shuíomh!',
      description: 'Cliceáil anseo chun leanúint ar aghaidh le táirgí agus praghsanna atá oiriúnaithe do do réigiún.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Cuardach Cumhachtaithe ag AI',
      description: 'Clóscríobh rud ar bith atá á lorg agat. Tuigeann ár AI teanga nádúrtha - bain triail as "an ríomhaire glúine is fearr le haghaidh cluichíochta faoi bhun $1000" nó "bronntanais do Mhamaí".',
    },
    demo: {
      title: 'Bain triail as cuardach taispeántais',
      description: 'Níl tú cinnte cad a chuardóidh tú? Cliceáil ar cheann de na samplaí seo chun a fheiceáil conas a aimsíonn ár AI na táirgí is fearr duit agus iad a chur i gcomparáid.',
    },
    languageSelector: {
      title: 'Athraigh teanga nó réigiún',
      description: 'Cliceáil anseo am ar bith chun do theanga a athrú nó táirgí ó réigiún eile a bhrabhsáil.',
    },
  },
};

// Hindi content
const hindiContent: TutorialUiContent = {
  stepIndicator: 'चरण {current} / {total}',
  skipButton: 'ट्यूटोरियल छोड़ें',
  regionStep: {
    title: 'अपना क्षेत्र चुनें',
    description: 'सर्वोत्तम स्थानीय उत्पाद और मूल्य प्राप्त करने के लिए अपना स्थान चुनें',
  },
  searchStep: {
    title: 'कुछ भी खोजें',
    description: 'उत्पादों को खोजने और तुलना करने के लिए हमारी AI-संचालित खोज का उपयोग करें',
  },
  continueWithRegion: '{region} के साथ जारी रखें',
  searchAnything: 'खोज शुरू करें',
  tryDemo: 'हमारा डेमो आज़माएँ!',
  welcome: 'Inception में आपका स्वागत है',
  nextButton: 'अगला',
  gotIt: 'समझ गया!',
  regionSpotlight: {
    globe: {
      title: 'दुनिया का अन्वेषण करें',
      description: 'ग्लोब घुमाएँ और अपने देश पर क्लिक करके उसे चुनें। आप जब चाहें वापस आकर उस देश का नक्शा खोल सकते हैं और अपना राज्य चुन सकते हैं — या पूरा देश ही रहने दें।',
    },
    regionList: {
      title: 'त्वरित क्षेत्र चयन',
      description: 'टाइप करना पसंद है? यहाँ कोई शहर या पता खोजें, या सूची में से सीधे अपना देश चुनें।',
    },
    yourRegion: {
      title: 'हमने आपका स्थान पहचान लिया!',
      description: 'आपके क्षेत्र के अनुकूल उत्पादों और मूल्यों के साथ जारी रखने के लिए यहाँ क्लिक करें।',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-संचालित खोज',
      description: 'आप जो खोज रहे हैं वह टाइप करें। हमारी AI प्राकृतिक भाषा समझती है - "₹50000 से कम में सबसे अच्छा गेमिंग लैपटॉप" या "माँ के लिए उपहार" आज़माएँ।',
    },
    demo: {
      title: 'डेमो खोज आज़माएँ',
      description: 'क्या खोजना है यह नहीं पता? हमारी AI आपके लिए सर्वोत्तम उत्पाद कैसे ढूँढती और तुलना करती है, यह देखने के लिए इन उदाहरणों में से एक पर क्लिक करें।',
    },
    languageSelector: {
      title: 'भाषा या क्षेत्र बदलें',
      description: 'अपनी भाषा बदलने या किसी अन्य क्षेत्र के उत्पाद ब्राउज़ करने के लिए कभी भी यहाँ क्लिक करें।',
    },
  },
};

// Basque content
const basqueContent: TutorialUiContent = {
  stepIndicator: '{current}. urratsa {total}(e)tik',
  skipButton: 'Tutoriala saltatu',
  regionStep: {
    title: 'Aukeratu zure eskualdea',
    description: 'Aukeratu zure kokapena tokiko produktu eta prezio onenak lortzeko',
  },
  searchStep: {
    title: 'Bilatu edozer gauza',
    description: 'Erabili gure AI bidezko bilaketa produktuak aurkitzeko eta konparatzeko',
  },
  continueWithRegion: '{region} eskualdearekin jarraitu',
  searchAnything: 'Bilatzen hasi',
  tryDemo: 'Probatu gure demoa!',
  welcome: 'Ongi etorri Inception-ra',
  nextButton: 'Hurrengoa',
  gotIt: 'Ulertuta!',
  regionSpotlight: {
    globe: {
      title: 'Esploratu mundua',
      description: 'Biratu globoa eskualde erabilgarri guztiak ikusteko. Egin klik nabarmendutako edozein herrialdetan hautatzeko.',
    },
    regionList: {
      title: 'Eskualde hautaketa azkarra',
      description: 'Edo egin klik zerrenda honetako eskualde batean.',
    },
    yourRegion: {
      title: 'Zure kokapena detektatu dugu!',
      description: 'Egin klik hemen zure eskualderako egokitutako produktu eta prezioekin jarraitzeko.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI bidezko bilaketa',
      description: 'Idatzi bilatzen duzuna. Gure AI-ak hizkuntza naturala ulertzen du - probatu "1000€ azpiko ordenagailu eramangarririk onena jokoetarako" edo "opariak amarentzat".',
    },
    demo: {
      title: 'Probatu demo bilaketa bat',
      description: 'Ez dakizu zer bilatu? Egin klik adibide hauetako batean gure AI-ak nola aurkitzen eta konparatzen dituen produktu onenak ikusteko.',
    },
    languageSelector: {
      title: 'Aldatu hizkuntza edo eskualdea',
      description: 'Egin klik hemen edozein unetan zure hizkuntza aldatzeko edo beste eskualde bateko produktuak arakatzeko.',
    },
  },
};

// Romansh content
const romanshContent: TutorialUiContent = {
  stepIndicator: 'Pass {current} da {total}',
  skipButton: 'Sursiglir il tutorial',
  regionStep: {
    title: 'Tschernai vossa regiun',
    description: 'Tschernai voss lieu per obtegnair ils megliers products e pretschs locals',
  },
  searchStep: {
    title: 'Tschertgai tge che vus vulais',
    description: 'Utilisai noss tschertga cun IA per chattar e cumparegliar products',
  },
  continueWithRegion: 'Cuntinuar cun {region}',
  searchAnything: 'Cumenzar a tschertgar',
  tryDemo: 'Empruvai noss demo!',
  welcome: 'Bainvegni tar Inception',
  nextButton: 'Enavant',
  gotIt: 'Chapì!',
  regionSpotlight: {
    globe: {
      title: 'Explorai il mund',
      description: 'Farai rotgar il glob per vesair tut las regiuns disponiblas. Cliccai sin in pajais illuminà per al tscherner.',
    },
    regionList: {
      title: 'Selecziun svelta da la regiun',
      description: 'U cliccai simplamain sin ina regiun da questa glista.',
    },
    yourRegion: {
      title: 'Nus avain chattà voss lieu!',
      description: 'Cliccai qua per cuntinuar cun products e pretschs adattads a vossa regiun.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'Tschertga cun IA',
      description: 'Scrivai tge che vus tschertgais. Nossa IA chapescha il linguatg natural - empruvai "il meglier laptop per gieus sut 1000 CHF" u "regals per la mamma".',
    },
    demo: {
      title: 'Empruvai ina tschertga demo',
      description: 'Betg segir tge tschertgar? Cliccai sin in dals exempels per vesair co nossa IA chatta e cumparegliescha ils megliers products per vus.',
    },
    languageSelector: {
      title: 'Midar la lingua u la regiun',
      description: 'Cliccai qua da tut temp per midar vossa lingua u navigar products dad ina autra regiun.',
    },
  },
};

// Telugu content
const teluguContent: TutorialUiContent = {
  stepIndicator: 'దశ {current} / {total}',
  skipButton: 'ట్యుటోరియల్ దాటవేయండి',
  regionStep: {
    title: 'మీ ప్రాంతాన్ని ఎంచుకోండి',
    description: 'ఉత్తమ స్థానిక ఉత్పత్తులు మరియు ధరలు పొందడానికి మీ లొకేషన్ ఎంచుకోండి',
  },
  searchStep: {
    title: 'ఏదైనా శోధించండి',
    description: 'ఉత్పత్తులను కనుగొనడానికి మరియు పోల్చడానికి మా AI-ఆధారిత శోధనను ఉపయోగించండి',
  },
  continueWithRegion: '{region} తో కొనసాగించండి',
  searchAnything: 'శోధన ప్రారంభించండి',
  tryDemo: 'మా డెమో ప్రయత్నించండి!',
  welcome: 'Inception కు స్వాగతం',
  nextButton: 'తదుపరి',
  gotIt: 'అర్థమైంది!',
  regionSpotlight: {
    globe: {
      title: 'ప్రపంచాన్ని అన్వేషించండి',
      description: 'అందుబాటులో ఉన్న అన్ని ప్రాంతాలను చూడటానికి గ్లోబ్ తిప్పండి. ఎంచుకోవడానికి ఏదైనా హైలైట్ చేయబడిన దేశంపై క్లిక్ చేయండి.',
    },
    regionList: {
      title: 'త్వరిత ప్రాంత ఎంపిక',
      description: 'లేదా ఈ జాబితా నుండి ఒక ప్రాంతంపై క్లిక్ చేయండి.',
    },
    yourRegion: {
      title: 'మీ లొకేషన్ గుర్తించబడింది!',
      description: 'మీ ప్రాంతానికి అనుగుణంగా ఉత్పత్తులు మరియు ధరలతో కొనసాగించడానికి ఇక్కడ క్లిక్ చేయండి.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-ఆధారిత శోధన',
      description: 'మీరు వెతుకుతున్నది ఏదైనా టైప్ చేయండి. మా AI సహజ భాషను అర్థం చేసుకుంటుంది — "$1000 లోపు గేమింగ్ కోసం ఉత్తమ ల్యాప్‌టాప్" లేదా "అమ్మ కోసం బహుమతులు" ప్రయత్నించండి.',
    },
    demo: {
      title: 'డెమో శోధన ప్రయత్నించండి',
      description: 'ఏమి శోధించాలో తెలియదా? మా AI మీకు ఉత్తమ ఉత్పత్తులను ఎలా కనుగొని పోలుస్తుందో చూడటానికి ఈ ఉదాహరణలలో ఒకదానిపై క్లిక్ చేయండి.',
    },
    languageSelector: {
      title: 'భాష లేదా ప్రాంతం మార్చండి',
      description: 'మీ భాషను మార్చడానికి లేదా వేరే ప్రాంతం నుండి ఉత్పత్తులను బ్రౌజ్ చేయడానికి ఏ సమయంలోనైనా ఇక్కడ క్లిక్ చేయండి.',
    },
  },
};

// Bengali content
const bengaliContent: TutorialUiContent = {
  stepIndicator: 'ধাপ {current} / {total}',
  skipButton: 'টিউটোরিয়াল এড়িয়ে যান',
  regionStep: {
    title: 'আপনার অঞ্চল নির্বাচন করুন',
    description: 'সেরা স্থানীয় পণ্য এবং মূল্য পেতে আপনার অবস্থান নির্বাচন করুন',
  },
  searchStep: {
    title: 'যেকোনো কিছু খুঁজুন',
    description: 'পণ্য খুঁজে পেতে এবং তুলনা করতে আমাদের AI-চালিত অনুসন্ধান ব্যবহার করুন',
  },
  continueWithRegion: '{region} নিয়ে চালিয়ে যান',
  searchAnything: 'অনুসন্ধান শুরু করুন',
  tryDemo: 'আমাদের ডেমো ব্যবহার করে দেখুন!',
  welcome: 'Inception-তে স্বাগতম',
  nextButton: 'পরবর্তী',
  gotIt: 'বুঝেছি!',
  regionSpotlight: {
    globe: {
      title: 'পৃথিবী অন্বেষণ করুন',
      description: 'সমস্ত উপলব্ধ অঞ্চল দেখতে গ্লোব ঘোরান। নির্বাচন করতে যেকোনো হাইলাইট করা দেশে ক্লিক করুন।',
    },
    regionList: {
      title: 'দ্রুত অঞ্চল নির্বাচন',
      description: 'অথবা এই তালিকা থেকে একটি অঞ্চলে ক্লিক করুন।',
    },
    yourRegion: {
      title: 'আমরা আপনার অবস্থান শনাক্ত করেছি!',
      description: 'আপনার অঞ্চলের জন্য উপযোগী পণ্য এবং মূল্য নিয়ে চালিয়ে যেতে এখানে ক্লিক করুন।',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-চালিত অনুসন্ধান',
      description: 'আপনি যা খুঁজছেন তা টাইপ করুন। আমাদের AI প্রাকৃতিক ভাষা বোঝে - "₹50000-এর নিচে সেরা গেমিং ল্যাপটপ" বা "মায়ের জন্য উপহার" চেষ্টা করুন।',
    },
    demo: {
      title: 'ডেমো অনুসন্ধান ব্যবহার করে দেখুন',
      description: 'কী খুঁজবেন জানেন না? আমাদের AI কীভাবে আপনার জন্য সেরা পণ্য খুঁজে বের করে এবং তুলনা করে তা দেখতে এই উদাহরণগুলির একটিতে ক্লিক করুন।',
    },
    languageSelector: {
      title: 'ভাষা বা অঞ্চল পরিবর্তন করুন',
      description: 'আপনার ভাষা পরিবর্তন করতে বা অন্য অঞ্চলের পণ্য ব্রাউজ করতে যেকোনো সময় এখানে ক্লিক করুন।',
    },
  },
};

// Marathi content
const marathiContent: TutorialUiContent = {
  stepIndicator: 'पायरी {current} / {total}',
  skipButton: 'ट्यूटोरियल वगळा',
  regionStep: {
    title: 'तुमचा प्रदेश निवडा',
    description: 'सर्वोत्तम स्थानिक उत्पादने आणि किमती मिळवण्यासाठी तुमचे स्थान निवडा',
  },
  searchStep: {
    title: 'काहीही शोधा',
    description: 'उत्पादने शोधण्यासाठी आणि तुलना करण्यासाठी आमचा AI-चालित शोध वापरा',
  },
  continueWithRegion: '{region} सह पुढे चालू ठेवा',
  searchAnything: 'शोध सुरू करा',
  tryDemo: 'आमचा डेमो वापरून पहा!',
  welcome: 'Inception मध्ये तुमचे स्वागत आहे',
  nextButton: 'पुढे',
  gotIt: 'समजले!',
  regionSpotlight: {
    globe: {
      title: 'जग एक्सप्लोर करा',
      description: 'सर्व उपलब्ध प्रदेश पाहण्यासाठी ग्लोब फिरवा. निवडण्यासाठी कोणत्याही हायलाइट केलेल्या देशावर क्लिक करा.',
    },
    regionList: {
      title: 'जलद प्रदेश निवड',
      description: 'किंवा या यादीतील एका प्रदेशावर क्लिक करा.',
    },
    yourRegion: {
      title: 'आम्ही तुमचे स्थान ओळखले!',
      description: 'तुमच्या प्रदेशासाठी अनुकूल उत्पादने आणि किमतींसह पुढे जाण्यासाठी येथे क्लिक करा.',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI-चालित शोध',
      description: 'तुम्ही शोधत असलेले टाइप करा. आमचा AI नैसर्गिक भाषा समजतो - "₹50000 पेक्षा कमी किमतीचा सर्वोत्तम गेमिंग लॅपटॉप" किंवा "आईसाठी भेटवस्तू" वापरून पहा.',
    },
    demo: {
      title: 'डेमो शोध वापरून पहा',
      description: 'काय शोधायचे माहीत नाही? आमचा AI तुमच्यासाठी सर्वोत्तम उत्पादने कशी शोधतो आणि तुलना करतो ते पाहण्यासाठी या उदाहरणांपैकी एकावर क्लिक करा.',
    },
    languageSelector: {
      title: 'भाषा किंवा प्रदेश बदला',
      description: 'तुमची भाषा बदलण्यासाठी किंवा दुसऱ्या प्रदेशातील उत्पादने ब्राउझ करण्यासाठी कधीही येथे क्लिक करा.',
    },
  },
};

// Japanese content
const japaneseContent: TutorialUiContent = {
  stepIndicator: 'ステップ {current} / {total}',
  skipButton: 'チュートリアルをスキップ',
  regionStep: {
    title: '地域を選択してください',
    description: 'お住まいの地域を選んで、最適な商品と価格を表示しましょう',
  },
  searchStep: {
    title: '何でも検索できます',
    description: 'AI搭載の検索機能で、商品を見つけて比較しましょう',
  },
  continueWithRegion: '{region}で続ける',
  searchAnything: '検索を始める',
  tryDemo: 'デモを試してみましょう！',
  welcome: 'Inceptionへようこそ',
  nextButton: '次へ',
  gotIt: 'わかりました！',
  regionSpotlight: {
    globe: {
      title: '世界を探索しましょう',
      description: '地球儀を回して、お住まいの国をクリックして選んでください。あとからいつでも戻って、その国の地図を開いて都道府県や州まで絞り込むことも、国全体のままにすることもできます。',
    },
    regionList: {
      title: 'クイック地域選択',
      description: '入力のほうが早い場合は、ここで都市名や住所を検索するか、リストから直接お住まいの国を選んでください。',
    },
    yourRegion: {
      title: 'お住まいの地域を検出しました！',
      description: 'こちらをクリックすると、お住まいの地域に合わせた商品と価格でお買い物を始められます。',
    },
  },
  searchSpotlight: {
    searchBar: {
      title: 'AI搭載検索',
      description: 'お探しのものを自由に入力してください。AIが自然な言葉を理解します。「10万円以下のゲーミングノートパソコン」や「お母さんへのプレゼント」などと検索してみてください。',
    },
    demo: {
      title: 'デモ検索を試す',
      description: '何を検索すればいいかわからない場合は、こちらのサンプルをクリックしてみてください。AIがどのように最適な商品を見つけて比較するかをご覧いただけます。',
    },
    languageSelector: {
      title: '言語・地域の変更',
      description: 'こちらをクリックすると、いつでも言語の変更や別の地域の商品の閲覧ができます。',
    },
  },
};

const tutorialUiContent: Record<LanguageCode, TutorialUiContent> = {
  [LanguageCode.english]: englishContent,
  [LanguageCode.french]: frenchContent,
  [LanguageCode.spanish]: spanishContent,
  [LanguageCode.german]: germanContent,
  [LanguageCode.italian]: italianContent,
  [LanguageCode.portugueseBR]: portugueseBRContent,
  [LanguageCode.portuguesePT]: portuguesePTContent,
  [LanguageCode.scottishGaelic]: scottishGaelicContent,
  [LanguageCode.galician]: galicianContent,
  [LanguageCode.swedish]: swedishContent,
  [LanguageCode.welsh]: welshContent,
  [LanguageCode.polish]: polishContent,
  [LanguageCode.dutch]: dutchContent,
  [LanguageCode.catalan]: catalanContent,
  [LanguageCode.maori]: maoriContent,
  [LanguageCode.tamil]: tamilContent,
  [LanguageCode.irish]: irishContent,
  [LanguageCode.hindi]: hindiContent,
  [LanguageCode.basque]: basqueContent,
  [LanguageCode.romansh]: romanshContent,
  [LanguageCode.telugu]: teluguContent,
  [LanguageCode.bengali]: bengaliContent,
  [LanguageCode.marathi]: marathiContent,
  [LanguageCode.japanese]: japaneseContent,
};

/**
 * Get tutorial-specific UI content based on locale
 * @param locale - The locale details to get content for
 * @returns TutorialUiContent object with localized content
 */
export function getTutorialUiContent(locale: LocaleDetails): TutorialUiContent {
  const languageCode = toCoreLanguageType(locale.languageCode || LanguageCode.english);
  return tutorialUiContent[languageCode];
}
